import {
  CpuChipIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  CloudArrowUpIcon,
  BoltIcon,
  UsersIcon,
  SparklesIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { PLATFORM_ENGINEERING_SECTIONS } from '../../../../content/enterpriseMessaging';
import { PLATFORM_ENGINEERING } from '../../../../content/staticAssets';
import AnimatedSectionImage from '../../shared/AnimatedSectionImage';
import MultiAgentArchitectureSchematic from './MultiAgentArchitectureSchematic';

const semanticItems = [
  {
    name: 'Enterprise semantic layer design',
    description:
      'Certified metrics, analytics engineering, and semantic foundations for enterprise intelligence, domain memory agents inherit instead of re-learning each report.',
    icon: ChartBarIcon,
    iconClass: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
  },
  {
    name: 'BI-to-data-layer transformation',
    description:
      'Move analytics from the BI layer to the Snowflake data layer, bridge dashboards, data, and business logic with governed models.',
    icon: BoltIcon,
    iconClass: 'bg-theme-migration-50 text-theme-migration-600',
  },
  {
    name: 'Streamlit on Snowflake',
    description:
      'Streamlit application engineering for operational and conversational experiences embedded in your Snowflake platform.',
    icon: Cog6ToothIcon,
    iconClass: 'bg-brand-50 text-brand-600',
  },
  {
    name: 'Governance & compliance',
    description:
      'Role hierarchies, masking, lineage, and audit-ready controls for secure enterprise analytics and AI-ready decisions.',
    icon: ShieldCheckIcon,
    iconClass: 'bg-accent-50 text-accent-700',
  },
];

const aiItems = [
  {
    name: 'Domain analyst agents',
    description:
      'Specialist SME agents bound to governed semantic views: finance, operations, workforce, and other domains, not a generic model over raw tables.',
    icon: CpuChipIcon,
    iconClass: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
  },
  {
    name: 'Master orchestrator',
    description:
      'Routes intent, plans multi-step work, delegates to domain sub-agents, and synthesizes answers within approved context.',
    icon: SparklesIcon,
    iconClass: 'bg-brand-50 text-brand-600',
  },
  {
    name: 'Governed MCP catalog',
    description:
      'Expose approved procedures as tools, including governed writes and seeds, so chat clients and third-party agents call specialists under your identity and policy, not as a SQL bypass.',
    icon: CloudArrowUpIcon,
    iconClass: 'bg-theme-migration-50 text-theme-migration-600',
  },
  {
    name: 'Chat-operated updates',
    description:
      'Operators ask, seed, and change warehouse facts source systems cannot store, through conversation, without writing SQL. High-stakes writes stay behind human oversight.',
    icon: CircleStackIcon,
    iconClass: 'bg-brand-50 text-brand-600',
  },
  {
    name: 'Human govern',
    description:
      'Closed-loop monitoring, Cortex, and decision support with human oversight on high-stakes actions. We do not promise fully autonomous decision-making.',
    icon: ChartBarIcon,
    iconClass: 'bg-accent-50 text-accent-700',
  },
];

const hrisItems = [
  {
    name: 'HRIS & HCM models',
    description:
      'Conformed people-data models that unify HCM, payroll, and talent into a single analytical layer on Snowflake.',
    icon: UsersIcon,
    iconClass: 'bg-brand-50 text-brand-600',
  },
  {
    name: 'Workforce intelligence dashboards',
    description:
      'Headcount, attrition, span-of-control, and DEI analytics built on governed workforce semantic layers, including Semantic HR™.',
    icon: CircleStackIcon,
    iconClass: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
  },
  {
    name: 'Operational command centers',
    description:
      'Real-time KPI ecosystems, flags, triggers, and leadership dashboards for operational and interactive analytics.',
    icon: ChartBarIcon,
    iconClass: 'bg-accent-50 text-accent-700',
  },
  {
    name: 'HRIS pipelines & automation',
    description:
      'Reliable extraction and modeling from leading HRIS platforms into Snowflake with governed domain memory.',
    icon: BoltIcon,
    iconClass: 'bg-theme-migration-50 text-theme-migration-600',
  },
];

function Block({ id, sectionKey, items, imageSrc, imageAlt, reverse, imageDelay = 0 }) {
  const section = PLATFORM_ENGINEERING_SECTIONS[sectionKey];
  const architecture = section.architecture;
  return (
    <div id={id} className="scroll-mt-24 space-y-10">
      <div className="grid grid-cols-1 items-start gap-x-12 gap-y-10 lg:grid-cols-2 lg:items-stretch">
        <div className={reverse ? 'lg:order-2 lg:self-stretch' : 'lg:self-stretch'}>
          <div className="lg:sticky lg:top-28">
            <AnimatedSectionImage src={imageSrc} alt={imageAlt} delay={imageDelay} />
          </div>
        </div>
        <div className={reverse ? 'lg:order-1' : ''}>
          <p className="eyebrow">{section.eyebrow}</p>
          <h3 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">{section.title}</h3>
          <p className="mt-4 text-lg leading-relaxed text-ink-600">{section.body}</p>
          {!architecture ? (
            <ItemGrid items={items} className="mt-8 sm:grid-cols-2" />
          ) : null}
        </div>
      </div>
      {architecture ? (
        <>
          <MultiAgentArchitectureSchematic
            steps={architecture.steps}
            quote={architecture.quote}
          />
          <ItemGrid items={items} className="sm:grid-cols-2 lg:grid-cols-3" />
        </>
      ) : null}
    </div>
  );
}

function ItemGrid({ items, className = '' }) {
  return (
    <dl className={`grid grid-cols-1 gap-x-6 gap-y-6 ${className}`}>
      {items.map((it) => (
        <div key={it.name} className="relative pl-10">
          <dt className="text-lg font-semibold text-ink-900">
            <span className={`absolute left-0 top-0 inline-flex h-7 w-7 items-center justify-center rounded-md ${it.iconClass || 'bg-brand-50 text-brand-600'}`}>
              <it.icon className="h-4 w-4" aria-hidden="true" />
            </span>
            {it.name}
          </dt>
          <dd className="mt-1 text-base text-ink-600 leading-relaxed">{it.description}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function Services() {
  return (
    <section className="section">
      <div className="container-x space-y-24">
        <Block
          id="modernization"
          sectionKey="modernization"
          items={semanticItems}
          imageSrc={PLATFORM_ENGINEERING.legacyModernization}
          imageAlt="Semantic layer and BI modernization"
          imageDelay={0}
        />
        <Block
          id="ai"
          sectionKey="ai"
          items={aiItems}
          imageSrc={PLATFORM_ENGINEERING.agenticRag}
          imageAlt="Snowflake enterprise AI and agents"
          reverse
          imageDelay={80}
        />
        <Block
          id="hris"
          sectionKey="hris"
          items={hrisItems}
          imageSrc={PLATFORM_ENGINEERING.crmHris}
          imageAlt="Workforce and operational intelligence"
          imageDelay={160}
        />
      </div>
    </section>
  );
}
